import {handleCrossRepositoryLinkClick} from '@github-ui/global-navigation/cross-repository-navigation'
import {type RefObject, useCallback, useEffect} from 'react'

import {useLayoutBaseRepository} from '../stores/layout-selectors'

/** The parts of a native or React click event used to decide whether to handle it. */
type LinkClickEvent = Parameters<typeof handleCrossRepositoryLinkClick>[0]

/**
 * Returns a click handler for links to an issue or pull request in another repository. On a plain click it keeps the
 * navigation in the mounted React app and refreshes the global navigation to the destination repository, instead of
 * the full page load that a navigation to another repository otherwise needs.
 *
 * The handler only prevents the link's default when that navigation starts, so modified clicks, other links, and
 * navigations the app can't handle (including while `global_nav_context_refresh` is disabled) keep the link's
 * existing behavior. Pass `anchor` when it isn't the closest link to the event target.
 */
export function useCrossRepositoryLinkClick() {
  const baseRepository = useLayoutBaseRepository()
  const currentRepositoryNameWithOwner = `${baseRepository.ownerLogin}/${baseRepository.name}`

  return useCallback(
    (event: LinkClickEvent, anchor?: HTMLAnchorElement | null) => {
      handleCrossRepositoryLinkClick(event, {anchor, currentRepositoryNameWithOwner})
    },
    [currentRepositoryNameWithOwner],
  )
}

/**
 * Handles clicks on links to an issue or pull request in another repository anywhere inside `containerRef`, such as
 * server-rendered comment bodies. The listener runs before Turbo's, which skips clicks whose default was prevented.
 */
export function useCrossRepositoryLinkInterception(containerRef: RefObject<HTMLElement | null>) {
  const handleClick = useCrossRepositoryLinkClick()

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const listener = (event: MouseEvent) => handleClick(event)
    container.addEventListener('click', listener)
    return () => container.removeEventListener('click', listener)
  }, [containerRef, handleClick])
}
